import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import { axiosCorrespondenciaInstance as axios, endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils-correspondencia';

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const USER_KEY = 'user';

export function AuthProviderCorrespondencia({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);
      const storedUser = sessionStorage.getItem(USER_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const user = storedUser ? JSON.parse(storedUser) : null;
        if (user) {
          dispatch({
            type: 'INITIAL',
            payload: {
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIAL',
            payload: {
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error('Error al inicializar la autenticación:', error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  // const login = useCallback(async (username, password) => {
  //   const data = { username, password };

  //   const response = await axios.post(endpoints.auth.login, data);
  //   const { access_token: accessToken, ...user } = response.data.data;
  //   setSession(accessToken);
  //   sessionStorage.setItem(USER_KEY, JSON.stringify(user)); // Almacenar datos del usuario en sessionStorage

  //   dispatch({
  //     type: 'LOGIN',
  //     payload: {
  //       user,
  //     },
  //   });
  // }, []);

  const login = useCallback(async (username, password) => {
    try {
      const data = { username, password };
      const response = await axios.post(endpoints.auth.login, data);
      // console.log('Respuesta del login:', response);

      const tokenData = response?.data?.data;

      if (!tokenData || !tokenData.access_token) {
        throw new Error('No se recibió access_token del servidor');
      }

      const { access_token: accessToken, ...user } = tokenData;

      setSession(accessToken);
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      throw error; // para que el componente que llama a `login()` lo maneje
    }
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    sessionStorage.removeItem(USER_KEY); // Eliminar datos del usuario del sessionStorage
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      logout,
    }),
    [login, logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProviderCorrespondencia.propTypes = {
  children: PropTypes.node,
};
